import React, { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { 
  Tag, Plus, Search, Filter, Clock, CheckCircle2, AlertTriangle, 
  Trash2, Edit3, Eye, RefreshCw, Sparkles, Layers, ShieldCheck, 
  X, Info, ArrowUpRight, DollarSign, Calendar, HelpCircle, Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import { 
  fetchPriceList, 
  createPriceListItem, 
  updatePriceListItem, 
  deletePriceListItem,
  CompanyPriceListItem, 
  PriceListPayload 
} from "../../services/priceListService";

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.03)] ${className}`}>
    {children}
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, maxWidth = "cht-max-w-xl" }) => {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-absolute cht-inset-0 cht-bg-slate-950/50 cht-backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ type: "spring", stiffness: 300, damping: 28 }}
            className={`cht-relative cht-w-full ${maxWidth} cht-bg-white cht-rounded-[32px] cht-p-7 cht-shadow-[0_25px_70px_rgba(0,0,0,0.15)] cht-max-h-[90vh] cht-overflow-y-auto cht-border cht-border-gray-100`}
          >
            <div className="cht-flex cht-justify-between cht-items-center cht-mb-5 cht-pb-3 cht-border-b cht-border-gray-100">
              <h2 className="cht-text-xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">{title}</h2>
              <button 
                onClick={onClose} 
                className="cht-p-2 cht-bg-gray-50 cht-rounded-xl cht-text-gray-400 hover:cht-text-gray-700 hover:cht-bg-gray-100 cht-transition-colors"
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const DEFAULT_CATEGORIES = [
  "CAC & Corporate Affairs",
  "Software & Web Development",
  "Graphics, Branding & Media",
  "ICT & Professional Trainings",
  "Printing & Stationeries",
  "General Business Services"
];

const AdminPriceList: React.FC = () => {
  const [items, setItems] = useState<CompanyPriceListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<string>("ALL");
  const [categories, setCategories] = useState<string[]>(DEFAULT_CATEGORIES);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [activeItem, setActiveItem] = useState<CompanyPriceListItem | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Form states
  const [formData, setFormData] = useState<PriceListPayload>({
    name: "",
    category: DEFAULT_CATEGORIES[0],
    standard_price: "",
    cost_price: "",
    wholesale_price: "",
    min_price: "",
    delivery_timeline: "3-5 Business Days",
    description: "",
    is_active: true,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchPriceList({
        search: searchQuery,
        category: selectedCategory !== "ALL" ? selectedCategory : undefined,
      });
      setItems(res.data || []);
      if (res.analytics?.categories && res.analytics.categories.length > 0) {
        setCategories(res.analytics.categories);
      }
    } catch (err) {
      console.error("Failed to load company price list:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Category filter
      if (selectedCategory !== "ALL" && item.category !== selectedCategory) return false;

      // Status filter
      if (selectedStatusFilter === "APPROVED" && (item.status !== "APPROVED" || item.change_status !== "NONE")) return false;
      if (selectedStatusFilter === "PENDING" && (item.status !== "PENDING_APPROVAL" && item.change_status === "NONE")) return false;
      if (selectedStatusFilter === "UPDATE_PENDING" && item.change_status !== "PENDING_UPDATE") return false;
      if (selectedStatusFilter === "DELETE_PENDING" && item.change_status !== "PENDING_DELETE") return false;

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = item.name.toLowerCase().includes(q);
        const matchCode = item.item_code.toLowerCase().includes(q);
        const matchCat = item.category.toLowerCase().includes(q);
        const matchDesc = (item.description || "").toLowerCase().includes(q);
        return matchName || matchCode || matchCat || matchDesc;
      }

      return true;
    });
  }, [items, selectedCategory, selectedStatusFilter, searchQuery]);

  // Open Create Modal
  const handleOpenAddModal = () => {
    setFormData({
      name: "",
      category: categories[0] || DEFAULT_CATEGORIES[0],
      standard_price: "",
      cost_price: "",
      wholesale_price: "",
      min_price: "",
      delivery_timeline: "3-5 Business Days",
      description: "",
      is_active: true,
    });
    setFeedbackMsg(null);
    setIsAddModalOpen(true);
  };

  // Open Edit Modal
  const handleOpenEditModal = (item: CompanyPriceListItem) => {
    setActiveItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      standard_price: item.standard_price,
      cost_price: item.cost_price,
      wholesale_price: item.wholesale_price || "",
      min_price: item.min_price || "",
      delivery_timeline: item.delivery_timeline || "",
      description: item.description || "",
      is_active: item.is_active,
    });
    setFeedbackMsg(null);
    setIsEditModalOpen(true);
  };

  // Submit Propose Addition
  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.standard_price) {
      setFeedbackMsg({ type: "error", text: "Service name and standard price are required." });
      return;
    }

    try {
      setSubmitting(true);
      const res = await createPriceListItem(formData);
      setFeedbackMsg({ type: "success", text: res.message || "Price item proposed successfully." });
      setTimeout(() => {
        setIsAddModalOpen(false);
        loadData();
      }, 1200);
    } catch (err: any) {
      console.error("Failed to propose price item:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to submit item proposal." });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Propose Update
  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeItem) return;

    try {
      setSubmitting(true);
      const res = await updatePriceListItem(activeItem.id, formData);
      setFeedbackMsg({ type: "success", text: res.message || "Proposed edits submitted for SuperAdmin review." });
      setTimeout(() => {
        setIsEditModalOpen(false);
        loadData();
      }, 1200);
    } catch (err: any) {
      console.error("Failed to propose edits:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to submit proposed edits." });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Propose Deletion
  const handleConfirmDelete = async () => {
    if (!activeItem) return;

    try {
      setSubmitting(true);
      const res = await deletePriceListItem(activeItem.id);
      setFeedbackMsg({ type: "success", text: res.message || "Deletion request submitted for SuperAdmin review." });
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        loadData();
      }, 1200);
    } catch (err: any) {
      console.error("Failed to request deletion:", err);
      setFeedbackMsg({ type: "error", text: err.response?.data?.detail || "Failed to submit deletion request." });
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = items.filter(i => i.status === "PENDING_APPROVAL" || i.change_status !== "NONE").length;

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
            <span className="cht-px-3 cht-py-1 cht-bg-blue-50 cht-text-[#3e57da] cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-rounded-xl">
              Corporate Catalog
            </span>
            <span className="cht-flex cht-items-center cht-gap-1 cht-text-xs cht-font-bold cht-text-gray-400">
              <Tag size={14} /> Price Governance
            </span>
          </div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
            Company Price List
          </h1>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm">
            Standard client retail billing rates, wholesale tiers, turnaround times, and official service offerings.
          </p>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            leftIcon={<RefreshCw size={16} className={refreshing ? "cht-animate-spin" : ""} />}
            className="cht-rounded-xl"
            title="Refresh price list"
          >
            Refresh
          </Button>

          <Button
            variant="primary"
            onClick={handleOpenAddModal}
            leftIcon={<Plus size={16} />}
            className="cht-rounded-xl"
          >
            Propose New Item
          </Button>
        </div>
      </header>

      {/* KPI Deck */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-5">
        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Active Services</span>
            <div className="cht-p-2.5 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-xl">
              <CheckCircle2 size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-2">
            {items.filter(i => i.status === "APPROVED" && i.is_active).length}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Live in corporate billing</p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Service Categories</span>
            <div className="cht-p-2.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-xl">
              <Layers size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-2">
            {categories.length}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Distinct service lines</p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Pending Review</span>
            <div className="cht-p-2.5 cht-bg-amber-50 cht-text-amber-600 cht-rounded-xl">
              <Clock size={18} />
            </div>
          </div>
          <h3 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-2">
            {pendingCount}
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Awaiting SuperAdmin action</p>
        </GlassCard>

        <GlassCard className="cht-p-5 cht-rounded-[24px]">
          <div className="cht-flex cht-items-center cht-justify-between">
            <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">Price Governance</span>
            <div className="cht-p-2.5 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-xl">
              <ShieldCheck size={18} />
            </div>
          </div>
          <h3 className="cht-text-base cht-font-black cht-text-indigo-900 cht-mt-2">
            Subject to SuperAdmin
          </h3>
          <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-mt-1">Edits staged safely</p>
        </GlassCard>
      </div>

      {/* Filter & Search Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search size={18} className="cht-absolute cht-left-3.5 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" />
          <input
            type="text"
            placeholder="Search price list by service name, item code, or scope..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-pl-10 cht-pr-4 cht-py-2.5 cht-bg-gray-50/50 cht-border cht-border-gray-200/70 cht-rounded-xl cht-text-xs cht-font-medium focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-outline-none"
          />
        </div>

        <div className="cht-flex cht-items-center cht-gap-3 cht-w-full md:cht-w-auto">
          {/* Category Filter */}
          <div className="cht-w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="cht-w-full cht-py-2.5 cht-px-3 cht-bg-white cht-border cht-border-gray-200/70 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-outline-none"
            >
              <option value="ALL">All Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="cht-w-44">
            <select
              value={selectedStatusFilter}
              onChange={(e) => setSelectedStatusFilter(e.target.value)}
              className="cht-w-full cht-py-2.5 cht-px-3 cht-bg-white cht-border cht-border-gray-200/70 cht-rounded-xl cht-text-xs cht-font-bold cht-text-gray-700 focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 focus:cht-border-[#3e57da] cht-outline-none"
            >
              <option value="ALL">All Statuses</option>
              <option value="APPROVED">Live & Approved</option>
              <option value="PENDING">Under Review (Any)</option>
              <option value="UPDATE_PENDING">Updates Under Review</option>
              <option value="DELETE_PENDING">Deletions Under Review</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Catalog Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        <div className="cht-p-5 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between">
          <div>
            <h4 className="cht-text-base cht-font-black cht-text-gray-900">
              Corporate Services & Pricing Catalog
            </h4>
            <p className="cht-text-xs cht-font-medium cht-text-gray-400">
              Approved rates are client-facing and used in invoices, work orders, and billing estimates.
            </p>
          </div>
          <span className="cht-text-xs cht-font-bold cht-text-gray-400">
            Showing {filteredItems.length} services
          </span>
        </div>

        <div className="cht-overflow-x-auto">
          <table className="cht-w-full cht-text-left cht-border-collapse">
            <thead>
              <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/40 cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                <th className="cht-py-4 cht-px-6">Code & Service</th>
                <th className="cht-py-4 cht-px-6">Category</th>
                <th className="cht-py-4 cht-px-6">Standard Price</th>
                <th className="cht-py-4 cht-px-6">Wholesale Price</th>
                <th className="cht-py-4 cht-px-6">Floor Price</th>
                <th className="cht-py-4 cht-px-6">Turnaround</th>
                <th className="cht-py-4 cht-px-6">Status</th>
                <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={8} className="cht-py-12 cht-text-center cht-text-gray-400">
                    <RefreshCw className="cht-animate-spin cht-mx-auto cht-mb-2 cht-text-[#3e57da]" size={24} />
                    <p className="cht-text-xs cht-font-bold">Loading company price list...</p>
                  </td>
                </tr>
              ) : filteredItems.length > 0 ? (
                filteredItems.map((item) => (
                  <tr key={item.id} className="hover:cht-bg-gray-50/60 cht-transition-colors">
                    <td className="cht-py-4 cht-px-6">
                      <div className="cht-flex cht-items-start cht-gap-2">
                        <span className="cht-text-[10px] cht-font-mono cht-font-black cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-700 cht-rounded-md">
                          {item.item_code}
                        </span>
                        <div>
                          <p className="cht-text-xs cht-font-black cht-text-gray-900">{item.name}</p>
                          {item.description && (
                            <p className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-line-clamp-1 max-w-xs">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-blue-50 cht-text-[#3e57da] cht-text-[11px] cht-font-black">
                        {item.category}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-text-xs cht-font-black cht-text-emerald-700">
                        ₦{Number(item.standard_price || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {item.wholesale_price ? (
                        <span className="cht-text-xs cht-font-bold cht-text-blue-700">
                          ₦{Number(item.wholesale_price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="cht-text-xs cht-text-gray-400">—</span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {item.min_price ? (
                        <span className="cht-text-xs cht-font-bold cht-text-gray-600">
                          ₦{Number(item.min_price).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="cht-text-xs cht-text-gray-400">—</span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[11px] cht-font-bold cht-text-gray-600">
                        <Clock size={12} className="cht-text-gray-400" />
                        {item.delivery_timeline || "Standard"}
                      </span>
                    </td>

                    <td className="cht-py-4 cht-px-6">
                      {item.change_status === "PENDING_DELETE" ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200 cht-text-[10px] cht-font-black" title="Deletion requested and awaiting SuperAdmin review">
                          <AlertTriangle size={11} />
                          Deletion Pending
                        </span>
                      ) : item.change_status === "PENDING_UPDATE" ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200 cht-text-[10px] cht-font-black" title="Edits staged awaiting SuperAdmin review">
                          <Edit3 size={11} />
                          Update Pending
                        </span>
                      ) : item.status === "PENDING_APPROVAL" ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200 cht-text-[10px] cht-font-black" title="Newly submitted item awaiting SuperAdmin publication">
                          <Clock size={11} />
                          Pending Review
                        </span>
                      ) : item.status === "REJECTED" ? (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-gray-100 cht-text-gray-700 cht-text-[10px] cht-font-black">
                          <X size={11} />
                          Rejected
                        </span>
                      ) : (
                        <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-emerald-50 cht-text-emerald-700 cht-text-[10px] cht-font-black">
                          <CheckCircle2 size={11} />
                          Active / Live
                        </span>
                      )}
                    </td>

                    <td className="cht-py-4 cht-px-6 cht-text-right">
                      <div className="cht-flex cht-items-center cht-justify-end cht-gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveItem(item);
                            setIsDetailModalOpen(true);
                          }}
                          className="cht-p-1.5 cht-text-gray-400 hover:cht-text-blue-600 hover:cht-bg-blue-50 cht-rounded-lg cht-transition-colors"
                          title="View Details"
                        >
                          <Eye size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleOpenEditModal(item)}
                          className="cht-p-1.5 cht-text-gray-400 hover:cht-text-[#3e57da] hover:cht-bg-blue-50 cht-rounded-lg cht-transition-colors"
                          title="Propose Edit"
                        >
                          <Edit3 size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setActiveItem(item);
                            setFeedbackMsg(null);
                            setIsDeleteModalOpen(true);
                          }}
                          className="cht-p-1.5 cht-text-gray-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-lg cht-transition-colors"
                          title="Request Deletion"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="cht-py-12 cht-text-center cht-text-gray-400">
                    <Tag size={32} className="cht-mx-auto cht-mb-2 cht-text-gray-300" />
                    <p className="cht-text-sm cht-font-bold cht-text-gray-600">No price list items found</p>
                    <p className="cht-text-xs cht-font-medium cht-text-gray-400 cht-mt-1">
                      Try adjusting filters or submit a new service proposal.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Propose New Item Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => !submitting && setIsAddModalOpen(false)}
        title="Propose New Price Item"
      >
        <form onSubmit={handleSubmitAdd} className="cht-space-y-4">
          <div className="cht-p-3 cht-bg-amber-50/80 cht-border cht-border-amber-200 cht-rounded-2xl cht-flex cht-items-start cht-gap-2.5">
            <Info size={16} className="cht-text-amber-700 cht-shrink-0 cht-mt-0.5" />
            <p className="cht-text-[11px] cht-font-medium cht-text-amber-800">
              Items proposed by Admin require SuperAdmin approval before appearing live on client-facing bills.
            </p>
          </div>

          {feedbackMsg && (
            <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
              feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
            }`}>
              {feedbackMsg.text}
            </div>
          )}

          <Input
            label="Service / Product Name"
            required
            placeholder="e.g. CAC Business Name Registration"
            value={formData.name}
            onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              as="select"
              label="Category"
              required
              value={formData.category}
              onChange={(e: any) => setFormData({ ...formData, category: e.target.value })}
              options={categories.map((c) => ({ label: c, value: c }))}
            />

            <Input
              label="Turnaround Timeline"
              placeholder="e.g. 3-5 Business Days"
              value={formData.delivery_timeline || ""}
              onChange={(e: any) => setFormData({ ...formData, delivery_timeline: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Standard Retail Price"
              prefixText="₦"
              type="number"
              step="0.01"
              required
              placeholder="25000.00"
              value={formData.standard_price}
              onChange={(e: any) => setFormData({ ...formData, standard_price: e.target.value })}
            />

            <Input
              label="Direct Cost / Filing Fee"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="10000.00"
              value={formData.cost_price || ""}
              onChange={(e: any) => setFormData({ ...formData, cost_price: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Wholesale Partner Rate"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="20000.00"
              value={formData.wholesale_price || ""}
              onChange={(e: any) => setFormData({ ...formData, wholesale_price: e.target.value })}
            />

            <Input
              label="Minimum Floor Price"
              prefixText="₦"
              type="number"
              step="0.01"
              placeholder="18000.00"
              value={formData.min_price || ""}
              onChange={(e: any) => setFormData({ ...formData, min_price: e.target.value })}
            />
          </div>

          <Input
            as="textarea"
            multiline
            rows={3}
            label="Description & Scope of Fulfillment"
            placeholder="Detail what is covered in this service fee..."
            value={formData.description || ""}
            onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="cht-flex cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsAddModalOpen(false)}
              disabled={submitting}
              className="cht-rounded-xl"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Submitting..." : "Submit Proposal"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Propose Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !submitting && setIsEditModalOpen(false)}
        title={`Propose Updates: ${activeItem?.item_code || ""}`}
      >
        <form onSubmit={handleSubmitEdit} className="cht-space-y-4">
          <div className="cht-p-3 cht-bg-indigo-50/80 cht-border cht-border-indigo-200 cht-rounded-2xl cht-flex cht-items-start cht-gap-2.5">
            <Info size={16} className="cht-text-indigo-700 cht-shrink-0 cht-mt-0.5" />
            <p className="cht-text-[11px] cht-font-medium cht-text-indigo-800">
              Current live rates remain active for all transactions until the SuperAdmin approves your proposed changes.
            </p>
          </div>

          {feedbackMsg && (
            <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
              feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
            }`}>
              {feedbackMsg.text}
            </div>
          )}

          <Input
            label="Service Name"
            required
            value={formData.name}
            onChange={(e: any) => setFormData({ ...formData, name: e.target.value })}
          />

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Standard Retail Price"
              prefixText="₦"
              type="number"
              step="0.01"
              required
              value={formData.standard_price}
              onChange={(e: any) => setFormData({ ...formData, standard_price: e.target.value })}
            />

            <Input
              label="Wholesale Partner Rate"
              prefixText="₦"
              type="number"
              step="0.01"
              value={formData.wholesale_price || ""}
              onChange={(e: any) => setFormData({ ...formData, wholesale_price: e.target.value })}
            />
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
            <Input
              label="Minimum Floor Price"
              prefixText="₦"
              type="number"
              step="0.01"
              value={formData.min_price || ""}
              onChange={(e: any) => setFormData({ ...formData, min_price: e.target.value })}
            />

            <Input
              label="Turnaround Timeline"
              value={formData.delivery_timeline || ""}
              onChange={(e: any) => setFormData({ ...formData, delivery_timeline: e.target.value })}
            />
          </div>

          <Input
            as="textarea"
            multiline
            rows={3}
            label="Description & Scope"
            value={formData.description || ""}
            onChange={(e: any) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="cht-flex cht-justify-end cht-gap-2.5 cht-pt-3 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              disabled={submitting}
              className="cht-rounded-xl"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="cht-rounded-xl"
            >
              {submitting ? "Submitting..." : "Propose Changes"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Item Details Modal */}
      {activeItem && (
        <Modal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          title={`Service Specification: ${activeItem.name}`}
        >
          <div className="cht-space-y-4">
            <div className="cht-bg-gray-50 cht-p-4 cht-rounded-2xl cht-space-y-2.5">
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Item Reference Code</span>
                <span className="cht-font-mono cht-font-black cht-text-gray-900">{activeItem.item_code}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Service Category</span>
                <span className="cht-font-black cht-text-[#3e57da]">{activeItem.category}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Turnaround Timeline</span>
                <span className="cht-font-bold cht-text-gray-700">{activeItem.delivery_timeline || "N/A"}</span>
              </div>
              <div className="cht-flex cht-justify-between cht-text-xs">
                <span className="cht-text-gray-400 cht-font-bold">Created By</span>
                <span className="cht-font-bold cht-text-gray-700">{activeItem.created_by_name || "Admin"}</span>
              </div>
              {activeItem.approved_by_name && (
                <div className="cht-flex cht-justify-between cht-text-xs">
                  <span className="cht-text-gray-400 cht-font-bold">Approved By</span>
                  <span className="cht-font-bold cht-text-emerald-700">{activeItem.approved_by_name}</span>
                </div>
              )}
            </div>

            {/* Pricing Matrix */}
            <div className="cht-grid cht-grid-cols-3 cht-gap-2.5 cht-text-center">
              <div className="cht-p-3 cht-bg-emerald-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-emerald-800 cht-uppercase">Standard Rate</span>
                <p className="cht-text-sm cht-font-black cht-text-emerald-700 cht-mt-1">
                  ₦{Number(activeItem.standard_price || 0).toLocaleString("en-NG")}
                </p>
              </div>

              <div className="cht-p-3 cht-bg-blue-50 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase">Wholesale Rate</span>
                <p className="cht-text-sm cht-font-black cht-text-blue-700 cht-mt-1">
                  {activeItem.wholesale_price ? `₦${Number(activeItem.wholesale_price).toLocaleString("en-NG")}` : "N/A"}
                </p>
              </div>

              <div className="cht-p-3 cht-bg-gray-100 cht-rounded-xl">
                <span className="cht-text-[10px] cht-font-black cht-text-gray-700 cht-uppercase">Floor Price</span>
                <p className="cht-text-sm cht-font-black cht-text-gray-800 cht-mt-1">
                  {activeItem.min_price ? `₦${Number(activeItem.min_price).toLocaleString("en-NG")}` : "N/A"}
                </p>
              </div>
            </div>

            {/* Description */}
            {activeItem.description && (
              <div className="cht-p-3.5 cht-border cht-border-gray-100 cht-rounded-2xl">
                <h5 className="cht-text-xs cht-font-black cht-text-gray-800 cht-mb-1">Fulfillment Deliverables</h5>
                <p className="cht-text-xs cht-text-gray-600 cht-leading-relaxed">{activeItem.description}</p>
              </div>
            )}

            {/* Staged Changes Notification if any */}
            {activeItem.change_status === "PENDING_UPDATE" && activeItem.pending_changes && (
              <div className="cht-p-3 cht-bg-indigo-50 cht-border cht-border-indigo-200 cht-rounded-2xl">
                <h5 className="cht-text-xs cht-font-black cht-text-indigo-900 cht-flex cht-items-center cht-gap-1">
                  <Edit3 size={13} /> Proposed Updates Awaiting SuperAdmin Review
                </h5>
                <div className="cht-mt-2 cht-space-y-1 cht-text-[11px]">
                  {Object.entries(activeItem.pending_changes).map(([k, v]) => (
                    <div key={k} className="cht-flex cht-justify-between cht-text-gray-600">
                      <span className="cht-capitalize">{k.replace('_', ' ')}:</span>
                      <span className="cht-font-bold cht-text-indigo-800">{String(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="cht-flex cht-justify-end cht-pt-2">
              <Button
                variant="outline"
                onClick={() => setIsDetailModalOpen(false)}
                className="cht-rounded-xl"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Confirm Deletion Request Modal */}
      {activeItem && (
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => !submitting && setIsDeleteModalOpen(false)}
          title="Confirm Deletion Request"
        >
          <div className="cht-space-y-4">
            <div className="cht-p-4 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-2xl cht-space-y-2">
              <div className="cht-flex cht-items-center cht-gap-2 cht-text-rose-800 cht-font-black cht-text-sm">
                <AlertTriangle size={18} /> Request Service Deletion
              </div>
              <p className="cht-text-xs cht-text-rose-700">
                Are you sure you want to request removing <strong className="cht-font-black">{activeItem.name}</strong> ({activeItem.item_code})?
              </p>
              <p className="cht-text-[11px] cht-text-rose-600 cht-font-medium">
                As an Admin, this deletion request will be forwarded to the SuperAdmin for approval before permanent removal.
              </p>
            </div>

            {feedbackMsg && (
              <div className={`cht-p-3 cht-rounded-xl cht-text-xs cht-font-bold ${
                feedbackMsg.type === "success" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-rose-50 cht-text-rose-700"
              }`}>
                {feedbackMsg.text}
              </div>
            )}

            <div className="cht-flex cht-justify-end cht-gap-2.5">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                disabled={submitting}
                className="cht-rounded-xl"
              >
                {submitting ? "Submitting..." : "Submit Deletion Request"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminPriceList;
